import { default as qs, StringifyOptions } from 'query-string';

import { AnyObject, Optional } from '@vimeo/core/types/generic';

import { environmentConfig } from '../../configs/EnvironmentConfig';
import { omit } from '../../utils/general/object';

/**
 * Encode provided object as query string value.
 * Resulting string looks like set of parameters concatenated with '&'.
 */
export function encodeQueryObject(source: AnyObject, options?: StringifyOptions): string {
  return qs.stringify(source, options);
}

/**
 * Encode provided object as full query string value.
 * Resulting string looks like set of parameters prefixed with '?' and concatenated with '&'.
 */
export function encodeQueryObjectAsString(
  source: Optional<AnyObject>,
  options?: StringifyOptions,
): string {
  const query: string = source ? qs.stringify(source, options) : '';
  return query ? '?' + query : '';
}

/**
 * Parse object to get URL query as object.
 */
export function getUrlQueryObject<T>(search: string = window.location.search): T {
  return qs.parse(search);
}

/**
 * Parse specified key for browser URL query.
 */
export function getUrlQueryValue<T>(key: string, search: string = location.search): Optional<T> {
  const query: AnyObject = qs.parse(search);

  if (key in query) {
    return query[key];
  }

  return null;
}

/**
 * Remove provided query param key and update current location.
 */
export function removeCurrentPathQueryValue(
  key: string,
  search: string = window.location.search,
): void {
  const hash: string = window.location.hash || '';
  const query: AnyObject = qs.parse(search);
  const nextQuery: string = qs.stringify(omit(query, key));
  const nextPath: string = `${environmentConfig.PROTOCOL}//${environmentConfig.HOST}${
    window.location.pathname
  }${nextQuery ? `?${nextQuery}` : ''}${hash}`;

  window.history.replaceState({}, '', nextPath);
}

/**
 * Add provided query param key and update current location.
 */
export function addCurrentPathQueryValue(
  key: string,
  value: string | number | boolean,
  search: string = window.location.search,
): void {
  const hash: string = window.location.hash || '';
  const query: AnyObject = qs.parse(search);
  const nextQuery: string = qs.stringify({ ...query, [key]: value });
  const nextPath: string = `${environmentConfig.PROTOCOL}//${environmentConfig.HOST}${
    window.location.pathname
  }${nextQuery ? `?${nextQuery}` : ''}${hash}`;

  window.history.replaceState({}, '', nextPath);
}
